import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, Box, Button, TextField, Typography } from "@mui/material";
import MaterialTable, { MTableAction } from "material-table";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  AddCtcCategoryAction,
  DeleteCtcCategoryAction,
  UpdateCtcCategoryAction,
} from "../../../../../store/ATS/CTCConfig/Action";
import { toast } from "react-toastify";

const CtcConfigurationGrid = () => {
  const dispatch = useDispatch();
  const { ctcCategory, isLoading } = useSelector(
    (state) => state.AtsReducer?.CtcCategoryReducer
  );
const validateCtcRange = (newRow, allRows, oldRow = null) => {
  const { category_label, min_range, max_range } = newRow;

  // --- Category validation ---
  if (!category_label?.trim()) {
    toast.error("Category label cannot be empty");
    return false;
  }

  // --- Min/Max numeric validation ---
  if (min_range === "" || min_range === null || isNaN(min_range)) {
    toast.error("Min range must be a valid number");
    return false;
  }
  if (max_range === "" || max_range === null || isNaN(max_range)) {
    toast.error("Max range must be a valid number");
    return false;
  }

  // --- Positive value check ---
  if (Number(min_range) < 0 || Number(max_range) < 0) {
    toast.error("Range values must be greater than 0");
    return false;
  }

  // --- Min < Max check ---
  if (Number(max_range) <= Number(min_range)) {
    toast.error("Max range must be greater than Min range");
    return false;
  }

  // --- Overlap validation ---
  const isOverlap = allRows?.some((row) => {
    // Skip the same row when editing
    if (oldRow && row.ctc_category_configuration_pk === oldRow.ctc_category_configuration_pk) return false;

    const existingMin = Number(row.min_range);
    const existingMax = Number(row.max_range);

    return (
      Number(min_range) <= existingMax && Number(max_range) >= existingMin
    );
  });

  if (isOverlap) {
    toast.error("This range overlaps with an existing category range");
    return false;
  }

  return true;
};

  return (
    <Box>
      <MaterialTable
        isLoading={isLoading}
        columns={[
          {
            title: "Category",
            cellStyle: { padding: "5px 10px 0px 28px" },
            field: "category_label",
            validate: (rowData) => {
              if (
                rowData?.tableData &&
                rowData?.tableData?.editing === "delete"
              ) {
                return "";
              }
              if (!rowData?.category_label?.trim()) {
                return "Category cannot be empty";
              }
              return "";
            },
            editComponent: (props) => {
              return (
                <Box sx={{ width: "95%" }}>
                  <TextField
                    required={true}
                    variant="standard"
                    fullWidth
                    value={props?.value}
                    onChange={(e) => props.onChange(e.target.value)}
                  />
                </Box>
              );
            },
          },
          {
            title: "Min Range",
            cellStyle: { padding: "5px 10px 0px 28px" },
            field: "min_range",
            validate: (rowData) => {
              if (
                rowData?.tableData &&
                rowData?.tableData?.editing === "delete"
              ) {
                return "";
              }
              const value = Number(rowData?.min_range);

              if (isNaN(value) || rowData?.min_range === "") {
                return "Min range cannot be empty";
              }
              if (value < 0) {
                return "Min range must be greater than 0";
              }
              return "";
            },
            editComponent: (props) => {
              return (
                <Box sx={{ width: "95%" }}>
                  <TextField
                    required={true}
                    variant="standard"
                    fullWidth
                    type="number"
                    value={props?.value}
                    onChange={(e) => {
                      const val = e.target.value;
                      props.onChange(val === "" ? "" : Number(val));
                    }}
                  />
                </Box>
              );
            },
          },
          {
            title: "Max Range",
            cellStyle: { padding: "5px 10px 0px 28px" },
            field: "max_range",
            validate: (rowData) => {
              if (rowData?.tableData?.editing === "delete") return "";
              const value = Number(rowData?.max_range);
              if (isNaN(value) || rowData?.max_range === "") {
                return "Max range cannot be empty";
              }
              if (value < 0) {
                return "Max range must be greater than 0";
              }
              return "";
            },
            render: (rowData) =>
              Number(rowData?.max_range) === 9999999999
                ? "Max"
                : rowData?.max_range,
            editComponent: (props) => {
              const maxRangeConstant = 9999999999;
              const allRows = ctcCategory || [];
            const isEdit = props.rowData?.tableData?.editing === "update" && props?.rowData?.max_range == maxRangeConstant;
              const hasMaxAlready = allRows.some(
                (row) => Number(row.max_range) === maxRangeConstant
              );

              const options = hasMaxAlready
                ? []
                : [{ label: "Max", value: maxRangeConstant }];

              // Determine display value
              const displayValue =
                props?.value == maxRangeConstant
                  ? "Max"
                  : props?.value?.toString() || "";

              // Validation check for showing error
              const isInvalid =
                typeof props.value === "number" &&
                props.value > maxRangeConstant;
              return (
                <Box sx={{ width: "95%" }}>
                  <Autocomplete
                    freeSolo
                    disableClearable
                    disabled={isEdit} 
                    options={options}
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.label
                    }
                    value={displayValue}
                    onChange={(e, newValue) => {
                      if (typeof newValue === "object" && newValue?.value) {
                        // When "Max-Limit" is selected
                        props.onChange(newValue.value);
                      } else if (typeof newValue === "string") {
                        const numVal = Number(newValue);
                        if (!isNaN(numVal)) {
                          props.onChange(numVal);
                        } else if (newValue === "") {
                          props.onChange("");
                        }
                      }
                    }}
                    onInputChange={(e, newInputValue, reason) => {
                      if (reason === "input") {
                        if (/^\d*$/.test(newInputValue)) {
                          const numVal = Number(newInputValue);
                          if (numVal < maxRangeConstant) {
                            props.onChange(numVal);
                          } else if (newInputValue === "") {
                            props.onChange("");
                          } else {
                            // Keep the value as it is, error will show
                            props.onChange(numVal);
                          }
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        fullWidth
                        required
                        error={isInvalid}
                        helperText={
                          isInvalid ? `Value must be less than Max value` : ""
                        }
                        inputProps={{
                          ...params.inputProps,
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          max: maxRangeConstant,
                          onKeyDown: (event) => {
                            const allowedKeys = [
                              "Backspace",
                              "ArrowLeft",
                              "ArrowRight",
                              "Delete",
                              "Tab",
                            ];
                            if (
                              !/[0-9]/.test(event.key) &&
                              !allowedKeys.includes(event.key)
                            ) {
                              event.preventDefault();
                            }
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              );
            },
          },
        ]}
        data={ctcCategory || []}
        icons={{
          Add: () => (
            <Button
              variant="contained"
              className="mpl-primary-btn"
              style={{
                minWidth: 0,
                width: "82px",
                height: "35px",
                padding: "2px 12px",
                textTransform: "none",
              }}
              startIcon={<AddIcon />}
            >
              Add
            </Button>
          ),
          Edit: () => <EditIcon sx={{ color: "#637381" }} />,
          Delete: () => <DeleteIcon sx={{ color: "#637381" }} />,
        }}
        editable={{
          onRowAdd: (newRow) =>
            new Promise((resolve, reject) => {
             const isValid = validateCtcRange(newRow, ctcCategory);
             if (!isValid) return reject();
              dispatch(
                AddCtcCategoryAction({
                  ...newRow,
                })
              );
              setTimeout(() => resolve(), 500);
            }),
          onRowUpdate: (newRow, oldRow) =>
            new Promise((resolve, reject) => {
            const isValid = validateCtcRange(newRow, ctcCategory, oldRow);
            if (!isValid) return reject();
              dispatch(
                UpdateCtcCategoryAction({
                  ...newRow,
                })
              );
              setTimeout(() => resolve(), 500);
            }),
          onRowDelete: (selectedRow) =>
            new Promise((resolve, reject) => {
              dispatch(
                DeleteCtcCategoryAction({
                  ...selectedRow,
                })
              );
              setTimeout(() => resolve(), 1000);
            }),
        }}
        options={{
          maxBodyHeight: "35vh",
          minBodyHeight: "35vh",
          rowStyle: { fontSize: "13px" },
          headerStyle: { padding: "5px 10px 5px 25px", fontWeight: "bold" },
          sorting: false,
          search: false,
          searchFieldAlignment: "right",
          searchAutoFocus: true,
          searchFieldVariant: "standard",
          filtering: false,
          paging: true,
          pageSizeOptions: [25, 50],
          pageSize: 25,
          selection: false,
          actionsColumnIndex: -1,
          grouping: false,
          addRowPosition: "first",
        }}
        components={{
          Action: (props) => {
            const ActionComponent = props?.components?.Action || MTableAction;

            if (
              props?.action?.icon &&
              typeof props?.action?.icon === "function"
            ) {
              return (
                <Box
                  sx={{
                    "& .MuiIconButton-root": {
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    },
                  }}
                >
                  <ActionComponent {...props} />
                </Box>
              );
            }
            return <ActionComponent {...props} />;
          },
        }}
        title={<Typography variant="body1" sx={{color: "inherit"}}>{"Defines CTC ranges and their corresponding tag/category. In the Mandate Report, the CTC Tag is automatically assigned based on the mandate's CTC value and the configured range."}</Typography>}
        
      />
    </Box>
  );
};
export default CtcConfigurationGrid;
